import React from "react";
import {createUseStyles} from "react-jss";
import cn from "classnames";

import NavbarMenuItem from "./NavbarMenuItem";
import NavbarAccountMenuItem from "~/components/navbar/NavbarAccountMenuItem";
import NavbarUpdatesWidget from "~/components/navbar/NavbarUpdatesWidget";
import UIIcon from "~/old-ui/UIIcon";
import NavbarLogo from "~/components/navbar/NavbarLogo";
import {isAdmin, isBrand, isCertifiedTrainer, isCompany, isReadonlyAccount, isTrainer} from '~/types/account';
import gql from "graphql-tag";
import NavbarNewEventMenuItem from "~/components/navbar/NavbarNewEventMenuItem";
import {mediaSizes, navbarBg2Color, navbarBgColor, shadowColor} from "~/theme";
import {externalUrl, isDev} from "~/utils";
import TrialNotificationBar from "~/components/TrialNotificationBar";
import UIContainer from "~/old-ui/UIContainer";
import {reverse} from "../../urls";

export default function Navbar({user, account, container: Container, className, ...props}) {
  const classes = useStyles();

  return (
    <>
      {account.isTrial && (
        <TrialNotificationBar
          account={account}
          container={Container}
        />
      )}
      <div className={cn(classes.root, className)} {...props}>
        <Container className={classes.container}>
          <div className={classes.mainArea}>
            <NavbarLogo className={classes.logo}/>

            <nav className={classes.mainMenu}>

              {isAdmin(account) && (
                <>
                  <NavbarMenuItem href={externalUrl('/accounts')} label='Accounts'/>
                  <NavbarMenuItem href={externalUrl('/invoicing')} label='Invoices'/>
                  <NavbarMenuItem href={externalUrl('/admin/accounts')} label='This month'/>
                </>
              )}

              {isBrand(account) && (
                <>
                  <NavbarMenuItem href={externalUrl('/dashboard')} label='Dashboard'/>
                  <NavbarMenuItem href={reverse('events_list')} label='Events'/>
                  <NavbarMenuItem href={reverse('contacts_list')} label='Contacts'/>
                  <NavbarMenuItem href={reverse('attendees_list')} label='Attendees'/>
                  <NavbarMenuItem href={externalUrl('/brand/statistics')} label='Statistics'/>
                  <NavbarMenuItem href={reverse('licensees_list')} label='Facilitators'/>
                  <NavbarMenuItem href={reverse('invoices_list')} label='Invoices'/>
                </>
              )}

              {isCompany(account) && (
                <>
                  <NavbarMenuItem href='/dashboard' external={false} label='Dashboard'/>
                  <NavbarMenuItem href={externalUrl('/events')} label='Events'/>
                  <NavbarMenuItem href={reverse('contacts_list')} label='Contacts'/>
                  <NavbarMenuItem href={externalUrl('/company/trainers')} label='Trainers'/>
                </>
              )}

              {isTrainer(account) && (
                <>
                  <NavbarMenuItem href='/dashboard' external={false} label='Dashboard'/>
                  <NavbarMenuItem href={externalUrl('/events')} label='Events'/>
                  <NavbarMenuItem href={reverse('contacts_list')} label='Contacts'/>
                  {!isDev() ? (
                    <NavbarMenuItem href={externalUrl('/trainer/attendees')} label='Attendees'/>
                  ) : (
                    <NavbarMenuItem external={false} href='/participations' label='Participations'/>
                  )}

                  {isCertifiedTrainer(account) && (
                    <>
                      <NavbarMenuItem href={externalUrl('/trainer/statistics')} label='Statistics'/>
                      <NavbarMenuItem href={externalUrl('/trainer/trainers')} label='Facilitators'/>
                    </>
                  )}
                </>
              )}
            </nav>

            <div className={classes.rightMenu}>
              {!isReadonlyAccount(account) && (
                <>
                  {(isTrainer(account) || isCompany(account)) && (
                    <NavbarNewEventMenuItem
                      accountId={account.id}
                    />
                  )}

                  {isBrand(account) && (
                    <a href={externalUrl("/brand/trainer/new")} className={classes.addTrainerItem}>
                      <UIIcon name='fa-user-plus' className={classes.addTrainerItemIcon}/>
                    </a>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={classes.accountArea}>
            <NavbarUpdatesWidget/>
            <NavbarAccountMenuItem
              user={user}
              account={account}
              className={classes.accountMenuItem}
            />
          </div>
        </Container>
      </div>
    </>
  )
}

Navbar.fragments = {
  user: gql`
    fragment NavbarUser on User {
      id
      __typename
      firstName
      isAdmin

      accounts {
        id
        __typename
        name
        image
      }
    }
  `,

  account: gql`
    fragment NavbarAccount on Account {
      id
      __typename
      name
      image

      ... on TrainerAccount {
        isCertified
      }

      isTrial
      trial {
        status
        endsIn
      }
    }
  `
};

Navbar.defaultProps = {
  container: UIContainer,
};

const useStyles = createUseStyles({
  root: {
    position: 'relative',
    height: 50,
    boxShadow: [[0, 2, 2, shadowColor]],

    fontFamily: ["Helvetica Neue", "Helvetica", "Arial", "sans-serif"],
    '-webkit-font-smoothing': 'antialiased',

    '&::before, &::after': {
      content: "\"\"",
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '50%',
      zIndex: -1,
    },

    '&::before': {
      left: 0,
      backgroundColor: navbarBgColor,
    },

    '&::after': {
      right: 0,
      backgroundColor: navbarBg2Color,
    }
  },

  container: {
    display: 'flex',
    height: '100%',
    justifyContent: 'space-between',
  },

  mainArea: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    flexGrow: 1,
    backgroundColor: navbarBgColor,
    paddingRight: 15,
  },

  logo: {
    marginRight: 12,
  },

  mainMenu: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },

  rightMenu: {
    display: 'flex',
    alignItems: 'center',

    [mediaSizes['md']]: {
      display: 'none',
    }
  },

  accountArea: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    paddingLeft: 30,

    '&::before': {
      content: "\"\"",
      position: 'absolute',
      left: 0,
      borderTop: [[25, 'solid', 'transparent']],
      borderLeft: [[15, 'solid', navbarBgColor]],
      borderBottom: [[25, 'solid', 'transparent']]
    }
  },

  accountMenuItem: {
    marginLeft: 12,
  },

  addTrainerItem: {
    color: '#fff',
    marginLeft: 10,
    width: 25,
    height: 25,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '&:hover': {
      opacity: 0.8,
      color: '#fff',
    }
  },

  addTrainerItemIcon: {
    width: 18,
    height: 18,
  },

});
